import { ContextWindow } from "@asup/context-menu";
import { useAppSettings, useTlfContext } from "contexts/AppSettingsContext";
import { getVariantPatchContext } from "contexts/tlf/getVariantPatchContext";
import { UPSERT_VARIANT_TEMPLATE } from "contexts/tlf/interface";
import { toArsTlfSnapshot } from "contexts/tlf/toArsTlfSnapshot";
import { eHierarchyLevel } from "functions/api/enums";
import { applyVariantPatches } from "functions/tlf/applyVariantPatches";
import { getDuplicateVariantName } from "functions/tlf/getDuplicateVariantName";
import { getVariantPatchGroupName } from "functions/tlf/getVariantPatchGroupName";
import { groupVariantPatches } from "functions/tlf/groupVariantPatches";
import { VariantTemplate } from "functions/tlf/isVariantTemplate";
import { ReactElement, useState } from "react";
import { TlfVariantNameSelector } from "./TlfVariantNameSelector";
import { TlfVariantPatchGroupRadio } from "./TlfVariantPatchGroupRadio";

/**
 * Pending patch choices remain local until their variant is committed.
 */
interface VariantSelectionDraft {
  masterId: string;
  template: VariantTemplate | undefined;
  patchNames: string[];
}

/**
 * Hosts variant selection, editing and application in the left-docked window.
 *
 * @returns The variant window, or nothing for non-masters or unsupported levels
 */
export const TlfVariantSidebar = (): ReactElement | null => {
  const tlfContext = useTlfContext();
  const { hierarchyState } = useAppSettings();
  const { currentMaster } = getVariantPatchContext(tlfContext);
  const level = hierarchyState.hierarchy?.level;
  const report = level === eHierarchyLevel.Report;
  const readOnly = tlfContext.readOnly;
  const design = !report && !readOnly;
  const templateLevel =
    level !== undefined &&
    [eHierarchyLevel.Corporate, eHierarchyLevel.TA, eHierarchyLevel.Project].includes(level);
  const appliedPatchKey = JSON.stringify([...tlfContext.appliedVariantPatches].sort());
  const initialName =
    currentMaster === null
      ? ""
      : (getDuplicateVariantName(
          currentMaster.variantTemplates,
          tlfContext.appliedVariantPatches,
          "",
        ) ?? "");
  const [selectedVariant, setSelectedVariant] = useState({
    masterId: currentMaster?.id ?? null,
    name: initialName,
  });
  const [draft, setDraft] = useState<VariantSelectionDraft | null>(null);
  const [appliedSelection, setAppliedSelection] = useState({
    masterId: currentMaster?.id,
    patchKey: appliedPatchKey,
  });
  if (
    appliedSelection.masterId !== currentMaster?.id ||
    appliedSelection.patchKey !== appliedPatchKey
  ) {
    setAppliedSelection({ masterId: currentMaster?.id, patchKey: appliedPatchKey });
    setSelectedVariant({ masterId: currentMaster?.id ?? null, name: initialName });
    setDraft(null);
  }
  if (
    currentMaster === null ||
    (!tlfContext.variantCreationView &&
      Object.keys(currentMaster.allVariantPatches).length === 0 &&
      currentMaster.variantTemplates.length === 0) ||
    (!report && !templateLevel)
  )
    return null;
  const selectedName =
    selectedVariant.masterId === currentMaster.id ? selectedVariant.name : initialName;
  const selectedTemplate = currentMaster.variantTemplates.find(
    (template) => template.name === selectedName,
  );
  const patchNames =
    draft !== null && draft.masterId === currentMaster.id && draft.template === selectedTemplate
      ? draft.patchNames
      : (selectedTemplate?.variantPatches ?? tlfContext.appliedVariantPatches);
  const groups = groupVariantPatches(currentMaster);
  const baseline =
    tlfContext.appliedVariantPatches.length === 0
      ? toArsTlfSnapshot(currentMaster)
      : tlfContext.masterTlfData;
  const compositionValid =
    baseline !== null &&
    patchNames.every((name) => Object.hasOwn(currentMaster.allVariantPatches, name)) &&
    groups.every((group) => group.filter((name) => patchNames.includes(name)).length <= 1) &&
    applyVariantPatches(baseline, currentMaster, patchNames).success;
  const radiosDisabled = readOnly || (design && selectedTemplate === undefined);

  /**
   * Replaces the changed group's choice while retaining independent selections.
   *
   * @param group - Mutually exclusive patch names in the changed group
   * @param patchName - Selected patch name, or empty string for None
   */
  const selectPatch = (group: string[], patchName: string): void => {
    const nextNames = patchNames.filter((name) => !group.includes(name));
    if (patchName !== "") nextNames.push(patchName);
    setDraft({ masterId: currentMaster.id, template: selectedTemplate, patchNames: nextNames });
    if (
      design &&
      selectedTemplate !== undefined &&
      getDuplicateVariantName(currentMaster.variantTemplates, nextNames, selectedTemplate.name) ===
        undefined
    ) {
      tlfContext.dispatch({
        operation: UPSERT_VARIANT_TEMPLATE,
        template: { ...selectedTemplate, variantPatches: nextNames },
        calledFrom: "TlfVariantSidebar:selectPatch",
      });
    }
  };

  return (
    <ContextWindow
      id="tlf-variants-window"
      title="Variants"
      visible
      initialDockEdge="left"
      style={{ width: "360px", maxWidth: "90vw", maxHeight: "80vh" }}
    >
      <div className="p-3 d-flex flex-column gap-3">
        <TlfVariantNameSelector
          compositionValid={compositionValid}
          patchNames={patchNames}
          selectedName={selectedName}
          onSelectVariant={(name) => {
            setSelectedVariant({ masterId: currentMaster.id, name });
            setDraft(null);
          }}
        />
        {groups.map((group, index) => (
          <TlfVariantPatchGroupRadio
            key={group[0]}
            patchNames={group}
            name={getVariantPatchGroupName(currentMaster, group)}
            selectedPatchNames={patchNames}
            disabled={radiosDisabled}
            warning={
              !compositionValid && index === 0
                ? "Selected patches cannot be applied to this master"
                : undefined
            }
            onSelectPatch={(patchName) => selectPatch(group, patchName)}
          />
        ))}
      </div>
    </ContextWindow>
  );
};

TlfVariantSidebar.displayName = "TlfVariantSidebar";
